import {AnalysisInfo, RcfaUserInfo} from '@domain/rcfa'
import {RcfaStoppageCode as StoppageCode} from '@domain/shared'
import {StoppageInfo} from '@domain/stoppages'
import {AnalysisType} from '@hconnect/common/types'
import {useUpmPlantsContext} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {debounce} from 'lodash'
import moment from 'moment'
import {useCallback, useEffect, useMemo, useState} from 'react'
import type {Dispatch, SetStateAction} from 'react'

import {
  nodeDescriptionValidation,
  nodeTitleValidation,
  shouldNotBeInPast,
  validateAll,
  type Validator
} from '../../utils/validator'

import {AnalysisToCreate} from './analysisCreate.types'
import {CreateAnalysisItem} from './createAnalysisForm.types'
import {useCreateAnalysis} from './useCreateAnalysis'
import type {RcfaCreatePlantOption} from './useRcfaCreatePlantOptions'

interface UseCreateAnalysisSubmissionProperties {
  item: CreateAnalysisItem
  customStoppage: StoppageCode | null
  externalStoppage?: StoppageInfo
  selectedPlantOption: RcfaCreatePlantOption | null
  analysisType?: AnalysisType
  onGoNext: () => void
  onAnalysisCreated: (analysis: AnalysisInfo) => void
  setItem: Dispatch<SetStateAction<CreateAnalysisItem>>
  t: (key: string) => string
}

interface UseCreateAnalysisSubmissionResult {
  validationError: Map<string, string>
  getErrorText: (key: string) => string | undefined
  doSubmit: () => void
  setOwner: (assignee: RcfaUserInfo | null) => void
  isLoading: boolean
  resetValidation: () => void
}

export const useCreateAnalysisSubmission = ({
  item,
  customStoppage,
  externalStoppage,
  selectedPlantOption,
  analysisType,
  onGoNext,
  onAnalysisCreated,
  setItem,
  t
}: UseCreateAnalysisSubmissionProperties): UseCreateAnalysisSubmissionResult => {
  const {mutate, isPending: isLoading} = useCreateAnalysis()
  const upmPlants = useUpmPlantsContext()
  const [validationError, setValidationError] = useState<Map<string, string>>(new Map())

  const validatorConfig = useMemo(
    () =>
      new Map<string, Validator>([
        ['description', nodeDescriptionValidation],
        ['title', nodeTitleValidation],
        ['dueDate', shouldNotBeInPast]
      ]),
    []
  )

  const debouncedMutate = useMemo(() => debounce(mutate, 500), [mutate])

  useEffect(() => () => debouncedMutate.cancel(), [debouncedMutate])

  const resetValidation = useCallback(() => {
    setValidationError(new Map())
  }, [])

  const buildOptionalParams = useCallback(
    (createObject: AnalysisToCreate): AnalysisToCreate => {
      if (externalStoppage?.stoppageCode?.code || customStoppage?.code) {
        createObject.stoppageCode = externalStoppage?.stoppageCode?.code ?? customStoppage?.code
        createObject.stoppageCodeType = externalStoppage?.stoppageCode?.type ?? customStoppage?.type
      }
      if (externalStoppage?.id) createObject.externalGroupId = externalStoppage?.externalGroupId
      if (item.stoppageStart) createObject.stoppageStart = item.stoppageStart
      if (item.stoppageEnd) createObject.stoppageEnd = item.stoppageEnd
      if (item.owner) createObject.owner = item.owner.id
      if (item.category) createObject.category = item.category
      if (item.areaType) createObject.areaTypeCode = item.areaType
      if (item.equipment) createObject.equipmentName = item.equipment

      return createObject
    },
    [customStoppage?.code, customStoppage?.type, externalStoppage, item]
  )

  const doSubmit = useCallback(() => {
    const validation = validateAll(validatorConfig, item)
    setValidationError(validation)

    if (validation.size !== 0 || moment(item.dueDate).isBefore(moment().startOf('day'))) {
      return
    }

    const createPlantUpmId = externalStoppage?.plantId
      ? upmPlants[externalStoppage.plantId]?.upmId
      : selectedPlantOption?.plantUpmId

    if (!createPlantUpmId) {
      spawnErrorToast(t('errors.createAnalysisPlantMissing'), {closeOnClick: true})
      return
    }

    const createObject: AnalysisToCreate = {
      title: item.title,
      description: item.description,
      privacyStatus: item.privacyStatus,
      type: analysisType,
      plantUpmId: createPlantUpmId,
      dueDate: item.dueDate.toISOString()
    }

    debouncedMutate(buildOptionalParams(createObject), {
      onSuccess: (data) => {
        onAnalysisCreated(data)
        onGoNext()
      }
    })
  }, [
    analysisType,
    buildOptionalParams,
    debouncedMutate,
    externalStoppage?.plantId,
    item,
    onAnalysisCreated,
    onGoNext,
    selectedPlantOption?.plantUpmId,
    t,
    upmPlants,
    validatorConfig
  ])

  const getErrorText = useCallback(
    (key: string) => {
      const errorKey = validationError.get(key)

      return errorKey && t(errorKey)
    },
    [t, validationError]
  )

  const setOwner = useCallback(
    (assignee: RcfaUserInfo | null) => {
      setItem((current) => ({...current, owner: assignee}))
    },
    [setItem]
  )

  return {
    validationError,
    getErrorText,
    doSubmit,
    setOwner,
    isLoading,
    resetValidation
  }
}
